import type { ReactNode } from 'react';
import { Stack, TextField, Toggle } from '@customerio/pluma-components/react';
import { Controller, useFormContext } from '@/components/form';
import { sanitizeHtml } from '@/utils/sanitize';
import type { IntegrationSetting } from '@/services/db/integration/entity';

/** A stored secret is never sent back, so the field stands in for it rather than showing a value. */
const SECRET_PLACEHOLDER = '***************************';

type SettingsFieldsProps = {
  settings: readonly IntegrationSetting[];
  isConnected: boolean;
  isDisabled: boolean;
};

/**
 * The connector's configuration inputs, generated from its catalog settings schema: booleans render
 * as toggles, everything else as a text field.
 */
export function SettingsFields({ settings, isConnected, isDisabled }: SettingsFieldsProps) {
  const { control } = useFormContext();

  return (
    <Stack gap="200">
      {settings.map((setting) => (
        <Controller
          key={setting.key}
          name={`settings.${setting.key}`}
          control={control}
          render={({ field, fieldState }) =>
            setting.type === 'boolean' ? (
              <Toggle
                size="sm"
                label={setting.label}
                description={settingDescription(setting)}
                isChecked={field.value === true}
                isDisabled={isDisabled}
                onChange={(event) => {
                  field.onChange(event.target.checked);
                }}
              />
            ) : (
              <TextSetting
                setting={setting}
                isConnected={isConnected}
                isDisabled={isDisabled}
                value={typeof field.value === 'string' ? field.value : ''}
                error={fieldState.error?.message}
                onChange={field.onChange}
                onBlur={field.onBlur}
              />
            )
          }
        />
      ))}
    </Stack>
  );
}

/**
 * Setting blurbs are server-authored catalog copy carrying "how to find your API key" links, so
 * they render as sanitized HTML. Ember sanitized the text-field blurbs but not the toggle ones;
 * both are sanitized here.
 */
function settingDescription(setting: IntegrationSetting): ReactNode {
  if (setting.description === '') {
    return undefined;
  }

  // nosemgrep: react-dangerouslysetinnerhtml — Customer.io's own catalog copy, sanitized via DOMPurify in sanitizeHtml()
  return <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(setting.description) }} />;
}

type TextSettingProps = {
  setting: IntegrationSetting;
  isConnected: boolean;
  isDisabled: boolean;
  value: string;
  error: string | undefined;
  onChange: (value: string) => void;
  onBlur: () => void;
};

function TextSetting({ setting, isConnected, isDisabled, value, error, onChange, onBlur }: TextSettingProps) {
  // A secret on a connected connector is optional and shows the mask instead of the stored value,
  // so it carries no validation state — there is nothing the user must correct.
  const isStoredSecret = setting.secret && isConnected;

  return (
    <TextField
      label={setting.label}
      description={settingDescription(setting)}
      data-test-data-out-field={setting.key}
      value={value}
      isDisabled={isDisabled}
      placeholder={
        isStoredSecret ? SECRET_PLACEHOLDER : setting.placeholder === '' ? setting.label : setting.placeholder
      }
      // The catalog's `type` describes the value ("string", "number"), not an HTML input type —
      // Ember passed it straight through, which produced an invalid `type="string"` attribute.
      type={setting.type === 'number' ? 'number' : 'text'}
      error={isStoredSecret ? undefined : error}
      onInput={(event) => {
        onChange(event.target.value);
      }}
      onBlur={onBlur}
    />
  );
}
